import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarCheck, Loader2, MapPin, PenLine, ShieldCheck, Trophy, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";

const POSITION_ICONS = {
  Forward: "FW",
  Midfielder: "MF",
  Defender: "DF",
  Goalkeeper: "GK",
};

const statColor = (value) => {
  if (value >= 80) return "bg-[hsl(142,72%,35%)]";
  if (value >= 60) return "bg-[hsl(38,92%,50%)]";
  return "bg-[hsl(var(--news-red))]";
};

const StatBar = ({ label, value, testId }) => (
  <div data-testid={testId} className="flex items-center gap-2">
    <span className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[hsl(var(--muted-foreground))] w-16 shrink-0">
      {label}
    </span>
    <div className="flex-1 h-2 rounded-full bg-[hsl(var(--surface-2))] overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${Math.min(100, (value / 99) * 100)}%` }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className={`h-full rounded-full ${statColor(value)}`}
      />
    </div>
    <span className="text-xs font-bold tabular-nums w-7 text-end text-[hsl(var(--ink))]">
      {value}
    </span>
  </div>
);

const StatBlock = ({ player, compact = false }) => {
  const { t } = useLang();
  if (!player.stats) return null;
  return (
    <div className={compact ? "space-y-1.5 mt-3" : "space-y-2"}>
      <StatBar label={t("pace")} value={player.stats.pace} testId="stat-bar-pace" />
      <StatBar label={t("defense")} value={player.stats.defense} testId="stat-bar-defense" />
      <StatBar label={t("passing")} value={player.stats.passing} testId="stat-bar-passing" />
    </div>
  );
};

const OriginBadge = ({ origin, small = false }) => {
  const { t } = useLang();
  if (!origin) return null;
  const base = small
    ? "inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
    : "inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm";
  if (origin === "GoodRec") {
    return (
      <span data-testid="scouting-origin-badge" className={`${base} bg-[hsl(24,94%,50%)] text-white`}>
        {t("goodRecBadge")}
      </span>
    );
  }
  if (origin === "CASA") {
    return (
      <span data-testid="scouting-origin-badge" className={`${base} bg-[hsl(43,90%,50%)] text-[hsl(222,47%,11%)]`}>
        {t("casaBadge")}
      </span>
    );
  }
  return null;
};


export const PlayerCard = ({ player, onSign, signing, signed = false }) => {
  const { t } = useLang();

  if (signed) {
    return (
      <Card
        data-testid="signed-player-card"
        className="scout-card relative overflow-hidden bg-[hsl(var(--card))] border-[hsl(var(--border))] shadow-sm"
      >
        {/* Dimmed card body */}
        <CardContent className="p-4 sm:p-5 opacity-60 grayscale">
          <div className="flex items-start justify-between gap-2 mb-3">
            <h3 className="text-base font-semibold leading-tight text-[hsl(var(--ink))]">
              {player.name}
            </h3>
            <Badge className="bg-[hsl(var(--surface-2))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--surface-2))] shrink-0 font-display tracking-widest">
              {t("signedBadge")}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-1.5 text-xs">
            <Badge
              variant="secondary"
              className="bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))]"
            >
              <MapPin className="h-3 w-3 me-1" />
              {player.city}
            </Badge>
            <Badge
              variant="secondary"
              className="bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))]"
            >
              {POSITION_ICONS[player.position]} · {player.position}
            </Badge>
            <Badge
              variant="secondary"
              className="bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))]"
            >
              {player.style}
            </Badge>
            <Badge
              variant="secondary"
              className="bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))]"
            >
              {player.commitment}
            </Badge>
            <Badge
              variant="secondary"
              className="bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))]"
            >
              {player.leaguePreference}
            </Badge>
            <OriginBadge origin={player.scoutingOrigin} small />
            {player.videoHighlight && (
              <Badge className="bg-[hsl(var(--navy))] text-white hover:bg-[hsl(var(--navy))]">
                🎬 {t("watchTape")}
              </Badge>
            )}
          </div>
          <StatBlock player={player} compact />
        </CardContent>

        {/* Diagonal TRANSFER COMPLETE stamp — centered + rotated, safe for LTR & RTL */}
        <motion.div
          data-testid="transfer-complete-banner"
          initial={{ opacity: 0, scale: 1.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.15 }}
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <div className="w-[150%] -rotate-6 bg-[hsl(var(--news-red))] text-white text-center py-1.5 shadow-lg border-y-2 border-white/30">
            <span className="font-display uppercase tracking-[0.2em] text-xs sm:text-sm font-bold whitespace-nowrap">
              🔒 {t("transferComplete")}
            </span>
          </div>
        </motion.div>
      </Card>
    );
  }

  return (
    <Card
      data-testid="free-agent-card"
      className="scout-card bg-[hsl(var(--card))] border-[hsl(var(--border))] shadow-sm hover:shadow-md h-full"
    >
      <CardContent className="p-5 flex flex-col h-full">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3
              data-testid="player-name"
              className="text-lg font-semibold leading-tight text-[hsl(var(--ink))]"
            >
              {player.name}
            </h3>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <p className="text-xs uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))] font-medium">
                {t("freeAgentTag")}
              </p>
              <span className="text-[hsl(var(--border))]">&bull;</span>
              <span
                data-testid="player-city"
                className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--navy))]"
              >
                <MapPin className="h-3 w-3 text-[hsl(var(--news-red))]" />
                {player.city}
              </span>
            </div>
          </div>
          <div className="shrink-0 h-11 w-11 rounded-lg bg-[hsl(var(--navy))] text-white flex items-center justify-center font-display text-lg">
            {POSITION_ICONS[player.position]}
          </div>
        </div>

        {/* Pro-pathway: Watch Tape + affiliation badges */}
        {(player.videoHighlight || player.scoutingOrigin) && (
          <div className="mb-4 -mt-1 flex flex-wrap gap-2">
            {player.videoHighlight &&
              (player.videoUrl ? (
                <a
                  data-testid="watch-tape-badge"
                  href={player.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-[hsl(var(--navy))] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm hover:bg-[hsl(var(--navy-2))] transition-colors"
                >
                  🎬 {t("watchTape")}
                </a>
              ) : (
                <span
                  data-testid="watch-tape-badge"
                  className="inline-flex items-center gap-1.5 bg-[hsl(var(--navy))] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm"
                >
                  🎬 {t("watchTape")}
                </span>
              ))}
            <OriginBadge origin={player.scoutingOrigin} />
          </div>
        )}

        <div className="space-y-2.5 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck className="h-4 w-4 text-[hsl(var(--navy))] shrink-0" />
            <span className="text-[hsl(var(--muted-foreground))]">{t("position")}</span>
            <Badge
              data-testid="player-position"
              className="ms-auto bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]"
            >
              {player.position}
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Zap className="h-4 w-4 text-[hsl(var(--navy))] shrink-0" />
            <span className="text-[hsl(var(--muted-foreground))]">{t("style")}</span>
            <Badge
              data-testid="player-style"
              className="ms-auto bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))] text-end"
            >
              {player.style}
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <CalendarCheck className="h-4 w-4 text-[hsl(var(--navy))] shrink-0" />
            <span className="text-[hsl(var(--muted-foreground))]">{t("commitment")}</span>
            <Badge
              data-testid="player-commitment"
              variant="secondary"
              className="ms-auto bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))] text-end"
            >
              {player.commitment}
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Trophy className="h-4 w-4 text-[hsl(var(--navy))] shrink-0" />
            <span className="text-[hsl(var(--muted-foreground))]">{t("leagueType")}</span>
            <Badge
              data-testid="player-league"
              className="ms-auto bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]"
            >
              {player.leaguePreference}
            </Badge>
          </div>
        </div>

        {/* FIFA-style stat bars */}
        <div className="border-t border-[hsl(var(--border))] pt-3 mb-5 flex-1">
          <StatBlock player={player} />
        </div>

        <Button
          data-testid="sign-full-season-button"
          onClick={() => onSign(player)}
          disabled={signing}
          className="w-full bg-[hsl(var(--news-red))] text-white hover:bg-[hsl(var(--news-red-dark))] focus-visible:ring-[hsl(var(--news-red))] active:scale-[0.98] transition-colors font-semibold h-11"
        >
          {signing ? (
            <Loader2 className="h-4 w-4 me-2 animate-spin" />
          ) : (
            <PenLine className="h-4 w-4 me-2" />
          )}
          {signing ? t("finalizingDeal") : t("signFullSeason")}
        </Button>
      </CardContent>
    </Card>
  );
};
